import type { JSX } from 'react';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import {
  Activity,
  BarChart3,
  BookOpen,
  Building,
  Building2,
  ChevronDown,
  ChevronLeft,
  CircleUserRound,
  UsersRound,
  ClipboardList,
  Contact,
  CreditCard,
  FlaskConical,
  Inbox,
  LayoutGrid,
  LibraryBig,
  LifeBuoy,
  ListChecks,
  ListTodo,
  LogOut,
  MoreHorizontal,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  RefreshCw,
  Settings as SettingsIcon,
  Target,
  TrendingUp,
  User,
  UserCheck,
  Users as UsersGroup,
  Workflow,
  ScrollText,
} from 'lucide-react';
import { Link, NavLink, useLocation, useMatch } from 'react-router-dom';

import { Brynn } from '../Brynn/Brynn';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../DropdownMenu/DropdownMenu';

import RevvlyMark from './revvly-mark.svg?react';
import { readSettingsReturnTo, writeSettingsReturnTo } from './settingsReturnTo';
import { useSidebarUI } from './SidebarUIContext';

import { TenantSwitcher } from '../TenantSwitcher/TenantSwitcher';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../hooks/useAuth';
import { useIsTenantAdmin } from '../../hooks/useIsTenantAdmin';
import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import {
  buildSettingsNav,
  findNavItem,
  settingsPageFromPath,
  type SettingsNavSection,
} from '../../pages/Settings/settingsNav';
import { isProspectRecord } from '../../pages/Organizations/isProspectRecord';
import type { OrganizationQuery } from '../../pages/Organizations/queries/organization.query.graphql';
import ORGANIZATION_QUERY from '../../pages/Organizations/queries/organization.query.graphql';
import { TenantFeature, TenantType } from '../../types/tenant.types';
import { websiteUrl } from '../../utils/env';
import { forceFrontendReload } from '../../utils/forceFrontendReload';

import type { SidebarCountsQuery } from './sidebarCounts.query.graphql';
import { KNOWLEDGE_HEALTH_QUERY } from '../../pages/Support/graphql/supportOperations';
import SIDEBAR_COUNTS_QUERY from './sidebarCounts.query.graphql';

import styles from './EnhancedSidebar.module.css';

type BadgeTone = 'neutral' | 'danger';

interface NavChildDef {
  id: string;
  label: string;
  path: string;
  end?: boolean;
  /** Same gating as `NavItemDef`: a child the tenant cannot use is not shown. */
  feature?: TenantFeature;
  anyFeature?: TenantFeature[];
  /** Small heading shown above the first child of each group (org settings sections). */
  group?: string;
  /** Overrides the path match when a child owns more than one route. */
  isActive?: (pathname: string) => boolean;
}

interface NavItemDef {
  id: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  path: string;
  feature?: TenantFeature;
  /** Shown when the tenant has at least one of these (OR). `feature` still means "must have". */
  anyFeature?: TenantFeature[];
  tenantTypes?: TenantType[];
  end?: boolean;
  children?: NavChildDef[];
  /** Overrides "a child path is the prefix" when the group owns routes of its own. */
  isActive?: (pathname: string) => boolean;
  /** Extra exact pathnames that should also mark this leaf item active
      (e.g. Ask Revvly lives at both `/assistant` and the `/` landing). */
  activePaths?: string[];
  /** Path prefixes that also mark this leaf active (a ticket page lights up Inbox). */
  activePrefixes?: string[];
}

interface NavSectionDef {
  id: string;
  title: string;
  items: NavItemDef[];
  feature?: TenantFeature;
  tenantTypes?: TenantType[];
}

const sidebarSections: NavSectionDef[] = [
  {
    id: 'assistant',
    title: 'Assistant',
    items: [
      {
        id: 'ask-revvly',
        label: 'Ask Revvly',
        // The animated Revvly AI mark (brand wave + spark) — not a lucide glyph.
        icon: Brynn,
        path: '/assistant',
        // The assistant is also the `/` landing page — light the row up there too.
        activePaths: ['/'],
      },
    ],
  },
  // The Accelerator section (Incubators + Users) is hidden for now — there is no
  // more incubator and it may return later (ClickUp 86cab5bnx). The page code and
  // routes are kept for a possible revival; only the nav entry is disabled here.
  // Users was relocated into the Admin section below so it stays reachable for the
  // AcceleratorApp tenant once this section is gone (ClickUp 86cab5btu).
  // {
  //   id: 'accelerator',
  //   title: 'Accelerator',
  //   tenantTypes: [TenantType.ACCELERATOR_APP],
  //   items: [
  //     {
  //       id: 'incubators',
  //       label: 'Incubators',
  //       icon: Briefcase,
  //       path: '/incubators',
  //       feature: TenantFeature.INCUBATORS,
  //     },
  //     {
  //       id: 'accelerator-users',
  //       label: 'Users',
  //       icon: UsersGroup,
  //       path: '/users',
  //       feature: TenantFeature.USER_MANAGEMENT,
  //     },
  //   ],
  // },
  {
    id: 'workspace',
    title: 'Workspace',
    items: [
      { id: 'analytics', label: 'Analytics', icon: TrendingUp, path: '/dashboard' },
      {
        // The person's own mail, sorted into categories. The Support section's
        // ticket queue is labelled "Support" so the two cannot be confused.
        id: 'work-inbox',
        label: 'Inbox',
        icon: Inbox,
        path: '/inbox',
        feature: TenantFeature.WORK_INBOX,
      },
      // Tasks is a baseline workspace feature — visible to every tenant,
      // independent of the CRM feature flag.
      { id: 'tasks', label: 'Tasks', icon: ListTodo, path: '/tasks' },
      { id: 'work-queue', label: 'Work queue', icon: ListChecks, path: '/work-queue' },
      {
        // Reusable content, one row with a sub-link per kind. Each child is
        // gated on its own feature; the row is hidden when none remain and
        // links to the first child that does.
        id: 'library',
        label: 'Library',
        icon: LibraryBig,
        path: '/library',
        children: [
          {
            // Shared documents for email attachments (CRM) and support replies.
            id: 'library-documents',
            label: 'Documents',
            path: '/library',
            anyFeature: [TenantFeature.CRM, TenantFeature.SUPPORT],
          },
          {
            id: 'library-forms',
            label: 'Forms',
            path: '/forms',
            feature: TenantFeature.FORMS,
          },
          {
            // Contract templates, sent for signature from a client's page.
            id: 'library-contracts',
            label: 'Contracts',
            path: '/contracts',
            feature: TenantFeature.FORMS,
          },
          {
            // Email templates (CRM) and payment reminders (Finance).
            id: 'library-templates',
            label: 'Templates',
            path: '/library/templates',
            anyFeature: [TenantFeature.CRM, TenantFeature.FINANCE],
          },
        ],
      },
    ],
  },
  {
    id: 'customers',
    title: 'Customers',
    items: [
      // Clients is always visible (the pre-CRM Organizations surface). The CRM
      // customer entities below are feature-gated with TenantFeature.CRM and
      // appear only when CRM is enabled.
      {
        id: 'organizations',
        label: 'Clients',
        icon: Building2,
        path: '/clients',
        // Situations are signals about clients (each also shows on its client's
        // page), so they sit under Clients rather than as their own item.
        // Ungated, matching `openAccountSituations`, which carries no
        // FeatureGateGuard. Empty until a mailbox is forwarding — the page
        // says so rather than reading as "all clear".
        children: [
          { id: 'clients-all', label: 'All clients', path: '/clients' },
          { id: 'clients-situations', label: 'Situations', path: '/situations' },
        ],
      },
      {
        id: 'companies',
        label: 'Companies',
        icon: Building,
        path: '/companies',
        feature: TenantFeature.CRM,
      },
      {
        id: 'contacts',
        label: 'Contacts',
        icon: Contact,
        path: '/contacts',
        feature: TenantFeature.CRM,
      },
      {
        id: 'pipeline',
        label: 'Pipeline',
        icon: Workflow,
        path: '/pipeline',
        feature: TenantFeature.CRM,
      },
    ],
  },
  {
    id: 'revenue',
    title: 'Revenue',
    items: [
      { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard, path: '/subscriptions' },
      {
        id: 'renewals',
        label: 'Renewals',
        icon: ClipboardList,
        path: '/renewals',
      },
      { id: 'finance', label: 'Finance', icon: Receipt, path: '/finance' },
    ],
  },
  {
    // Its own section: the agents who work tickets all day should not hunt
    // for them among the CRM entries.
    id: 'support',
    title: 'Support',
    feature: TenantFeature.SUPPORT,
    items: [
      {
        id: 'support-inbox',
        label: 'Support',
        icon: LifeBuoy,
        path: '/support',
        end: true,
        activePrefixes: ['/support/tickets/', '/support/notifications', '/support/sandbox'],
      },
      { id: 'support-people', label: 'End users', icon: UsersRound, path: '/support/end-users' },
      { id: 'support-knowledge', label: 'Knowledge', icon: BookOpen, path: '/support/knowledge' },
      {
        id: 'support-guidelines',
        label: 'Guidelines',
        icon: ScrollText,
        path: '/support/guidelines',
      },
      { id: 'support-metrics', label: 'Metrics', icon: BarChart3, path: '/support/metrics' },
    ],
  },
  {
    id: 'strategy',
    title: 'Strategy',
    items: [
      {
        id: 'okr-cycle',
        label: 'OKR Cycle',
        icon: Target,
        path: '/okr',
        // Shown once OKRs are granted, turned on or not. Waiting for
        // activation too made granting them look like it had done nothing;
        // an entitled tenant that has not turned them on lands on a page
        // that offers the switch (OkrTurnOnPrompt).
        // `/okr` alone: Coverage lives under it, and a prefix match would light
        // both rows. The pages that belong to the cycle are named instead, so
        // an objective or a key result still keeps this row lit.
        end: true,
        activePrefixes: ['/okr/objectives/', '/okr/key-results/', '/okr/kr/'],
        feature: TenantFeature.OKR,
      },
      {
        id: 'my-okrs',
        label: 'My OKRs',
        icon: UserCheck,
        path: '/my-okrs',
        // Same gate as OKR Cycle: an entitled tenant that has not turned OKRs
        // on is offered the switch on this page too.
        feature: TenantFeature.OKR,
      },
      {
        id: 'okr-hypotheses',
        label: 'Hypotheses',
        icon: FlaskConical,
        path: '/okr/hypotheses',
        feature: TenantFeature.OKR,
      },
      {
        id: 'okr-coverage',
        label: 'Coverage',
        icon: Network,
        path: '/okr/coverage',
        feature: TenantFeature.OKR,
      },
      {
        // How customers use the product, from the events it sends. It informs what to build and
        // whom to help, so it sits with the strategy tools rather than under Clients. Hidden
        // without the feature. `/product-usage/users/...` (a person across clients) keeps it lit.
        id: 'product-usage',
        label: 'Product usage',
        icon: Activity,
        path: '/product-usage',
        feature: TenantFeature.PRODUCT_USAGE,
      },
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    items: [
      {
        id: 'catalog',
        label: 'Catalog',
        icon: LayoutGrid,
        path: '/catalog',
        children: [
          { id: 'catalog-markets', label: 'Regions', path: '/market-config' },
          { id: 'catalog-products', label: 'Products', path: '/catalog' },
          { id: 'catalog-discounts', label: 'Discounts', path: '/discounts' },
          {
            id: 'catalog-feature-flags',
            label: 'Feature Flags',
            path: '/product-config/feature-flags',
          },
        ],
      },
      {
        id: 'admin-users',
        label: 'Users',
        icon: UsersGroup,
        path: '/users',
        // User management is available to every tenant, surfaced here between
        // Catalog and Settings. It is also reachable via the Settings > Users tab.
      },
      { id: 'settings', label: 'Org Settings', icon: SettingsIcon, path: '/settings' },
    ],
  },
];

interface OrgScopedItemDef {
  id: string;
  label: string;
  tab: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  group: 'overview' | 'history';
  feature?: TenantFeature;
  /** Hidden on a pre-sale record, which has no such tab. */
  prospectHidden?: boolean;
}

const orgScopedItems: OrgScopedItemDef[] = [
  { id: 'overview', label: 'Overview', tab: 'overview', icon: LayoutGrid, group: 'overview' },
  { id: 'contacts', label: 'Contacts', tab: 'contacts', icon: CircleUserRound, group: 'overview' },
  {
    id: 'tasks',
    label: 'Tasks',
    tab: 'tasks',
    icon: ListTodo,
    group: 'overview',
  },
  {
    id: 'subscriptions',
    label: 'Subscriptions',
    tab: 'subscriptions',
    icon: CreditCard,
    group: 'overview',
    prospectHidden: true,
  },
  {
    id: 'billing',
    label: 'Billing',
    tab: 'billing',
    icon: Receipt,
    group: 'overview',
    prospectHidden: true,
  },
  { id: 'activity', label: 'Activity', tab: 'activity', icon: Activity, group: 'history' },
];

function formatCount(value: number | undefined | null): string | null {
  if (value === undefined || value === null) return null;
  if (value <= 0) return null;
  if (value > 999) return `${Math.floor(value / 100) / 10}k`;
  return String(value);
}

function getInitials(name: string | undefined): string {
  if (!name) return '·';
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('') || '·'
  );
}

function getEnvLabel(): string | null {
  const mode = import.meta.env.MODE;
  if (mode === 'production') return null;
  if (mode === 'staging') return 'STAGING';
  return 'DEV';
}

interface CollapsedLabelPopoverProps {
  label: string;
  enabled: boolean;
  children: React.ReactNode;
}

/**
 * Hover label for a collapsed-rail item without a submenu: the submenu flyout
 * with the title only, portalled so the rail can't clip it.
 */
function CollapsedLabelPopover({
  label,
  enabled,
  children,
}: CollapsedLabelPopoverProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);

  const rect = open && groupRef.current ? groupRef.current.getBoundingClientRect() : null;

  return (
    <div
      ref={groupRef}
      className={styles.NavGroup}
      onMouseEnter={enabled ? () => setOpen(true) : undefined}
      onMouseLeave={enabled ? () => setOpen(false) : undefined}
    >
      {children}
      {enabled &&
        open &&
        rect &&
        createPortal(
          <div
            className={styles.NavGroupFlyout}
            style={
              {
                '--flyout-top': `${rect.top}px`,
                '--flyout-left': `${rect.right + 8}px`,
              } as React.CSSProperties
            }
            role="tooltip"
          >
            <span className={styles.NavGroupFlyoutTitle}>{label}</span>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface NavRowProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  badge?: string | null;
  badgeTone?: BadgeTone;
  isCollapsed: boolean;
  onNavigate: () => void;
  end?: boolean;
  forceActive?: boolean;
}

function NavRow({
  to,
  icon: Icon,
  label,
  badge,
  badgeTone = 'neutral',
  isCollapsed,
  onNavigate,
  end,
  forceActive,
}: NavRowProps): JSX.Element {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        classNames(styles.NavRow, {
          [styles.NavRowActive]: isActive || forceActive,
          [styles.NavRowCollapsed]: isCollapsed,
        })
      }
    >
      <span className={styles.NavRowIcon}>
        <Icon aria-hidden="true" />
      </span>
      <span className={styles.NavRowLabel}>{label}</span>
      {badge && (
        <span
          className={classNames(styles.NavBadge, {
            [styles.NavBadgeDanger]: badgeTone === 'danger',
          })}
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

interface NavItemProps {
  item: NavItemDef;
  badge: { value: string; tone?: BadgeTone } | undefined;
  isCollapsed: boolean;
  onNavigate: () => void;
}

function NavItem({ item, badge, isCollapsed, onNavigate }: NavItemProps): JSX.Element {
  const { pathname } = useLocation();
  const hasChildren = !!item.children?.length;
  const childActive =
    hasChildren &&
    (item.isActive
      ? item.isActive(pathname)
      : item.children.some((child) => pathname.startsWith(child.path)));
  const [expanded, setExpanded] = useState<boolean>(childActive);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const groupRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep expanded state in sync when route changes to a child path.
  useEffect(() => {
    if (childActive) setExpanded(true);
  }, [childActive]);

  const openFlyout = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setFlyoutOpen(true);
  };

  const closeFlyout = () => {
    closeTimer.current = setTimeout(() => setFlyoutOpen(false), 120);
  };

  const flyoutPosition = () => {
    if (!groupRef.current) return { top: 0, left: 0 };
    const rect = groupRef.current.getBoundingClientRect();
    return { top: rect.top, left: rect.right + 8 };
  };

  if (!hasChildren) {
    // Collapsed leaf items reuse the submenu flyout (title only), so the hover
    // label looks the same as for a group and isn't clipped by the rail.
    return (
      <CollapsedLabelPopover label={item.label} enabled={isCollapsed}>
        <NavRow
          to={item.path}
          icon={item.icon}
          label={item.label}
          badge={badge?.value ?? null}
          badgeTone={badge?.tone}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          end={item.end}
          forceActive={
            (item.activePaths?.includes(pathname) ?? false) ||
            (item.activePrefixes?.some((prefix) => pathname.startsWith(prefix)) ?? false)
          }
        />
      </CollapsedLabelPopover>
    );
  }

  return (
    <div
      ref={groupRef}
      className={styles.NavGroup}
      onMouseEnter={isCollapsed && hasChildren ? openFlyout : undefined}
      onMouseLeave={isCollapsed && hasChildren ? closeFlyout : undefined}
    >
      <div className={styles.NavGroupRow}>
        <NavRow
          to={item.path}
          icon={item.icon}
          label={item.label}
          badge={badge?.value ?? null}
          badgeTone={badge?.tone}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          end={item.end}
          forceActive={childActive}
        />
        {!isCollapsed && (
          <button
            type="button"
            className={classNames(styles.NavGroupChevron, {
              [styles.NavGroupChevronExpanded]: expanded,
            })}
            aria-expanded={expanded}
            aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            <ChevronDown aria-hidden="true" />
          </button>
        )}
      </div>
      {!isCollapsed && expanded && (
        <ul className={styles.NavChildList}>
          {item.children.map((child, index) => (
            <Fragment key={child.id}>
              {child.group && child.group !== item.children[index - 1]?.group && (
                <li className={styles.NavChildGroup} role="presentation">
                  {child.group}
                </li>
              )}
              <li className={styles.NavChildItem}>
                <NavLink
                  to={child.path}
                  end={child.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    classNames(styles.NavChild, {
                      [styles.NavChildActive]: child.isActive ? child.isActive(pathname) : isActive,
                    })
                  }
                >
                  <span className={styles.NavChildLabel}>{child.label}</span>
                </NavLink>
              </li>
            </Fragment>
          ))}
        </ul>
      )}
      {isCollapsed &&
        flyoutOpen &&
        createPortal(
          <div
            className={styles.NavGroupFlyout}
            style={
              {
                '--flyout-top': `${flyoutPosition().top}px`,
                '--flyout-left': `${flyoutPosition().left}px`,
              } as React.CSSProperties
            }
            onMouseEnter={openFlyout}
            onMouseLeave={closeFlyout}
            role="menu"
            tabIndex={-1}
          >
            <span className={styles.NavGroupFlyoutTitle}>{item.label}</span>
            <ul className={styles.NavGroupFlyoutList}>
              {item.children.map((child, index) => (
                <Fragment key={child.id}>
                  {child.group && child.group !== item.children[index - 1]?.group && (
                    <li className={styles.NavGroupFlyoutGroup} role="presentation">
                      {child.group}
                    </li>
                  )}
                  <li>
                    <NavLink
                      to={child.path}
                      end={child.end}
                      onClick={() => {
                        setFlyoutOpen(false);
                        onNavigate();
                      }}
                      role="menuitem"
                      className={({ isActive }) =>
                        classNames(styles.NavGroupFlyoutItem, {
                          [styles.NavGroupFlyoutItemActive]: child.isActive
                            ? child.isActive(pathname)
                            : isActive,
                        })
                      }
                    >
                      {child.label}
                    </NavLink>
                  </li>
                </Fragment>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface OrgScopedNavProps {
  orgId: string;
  orgName: string | undefined;
  orgInitials: string;
  arrLabel: string | null;
  openInvoices: number;
  activeSubs: number;
  overdueInvoices: number;
  /**
   * Pre-sale record: drops the billing sections, since the prospect page has no
   * Subscriptions or Billing tab to navigate to, and sends the back link to the
   * list the record actually came from.
   */
  isProspect: boolean;
  isCollapsed: boolean;
  onNavigate: () => void;
}

function OrgScopedNav({
  orgId,
  orgName,
  orgInitials,
  arrLabel,
  openInvoices,
  activeSubs,
  overdueInvoices,
  isProspect,
  isCollapsed,
  onNavigate,
}: OrgScopedNavProps): JSX.Element {
  const { hasFeature } = useTenantFeatures();
  const visibleItems = orgScopedItems.filter(
    (item) => (!item.feature || hasFeature(item.feature)) && !(isProspect && item.prospectHidden),
  );
  const overviewItems = visibleItems.filter((item) => item.group === 'overview');
  const historyItems = visibleItems.filter((item) => item.group === 'history');

  const badgeFor = (id: string): { value: string; tone: BadgeTone } | null => {
    if (id === 'subscriptions' && activeSubs > 0) {
      return { value: String(activeSubs), tone: 'neutral' };
    }
    if (id === 'billing' && overdueInvoices > 0) {
      return { value: String(overdueInvoices), tone: 'danger' };
    }
    return null;
  };

  // Non-client companies are listed on /companies, which is CRM-gated; without
  // CRM the clients list is the only list that exists.
  const backTo = isProspect && hasFeature(TenantFeature.CRM) ? '/companies' : '/clients';
  const backLabel = backTo === '/companies' ? 'All companies' : 'All clients';

  return (
    <div className={styles.ScopedNav}>
      <CollapsedLabelPopover label={backLabel} enabled={isCollapsed}>
        <NavLink
          to={backTo}
          onClick={onNavigate}
          className={classNames(styles.BackLink, {
            [styles.BackLinkCollapsed]: isCollapsed,
          })}
        >
          <ChevronLeft aria-hidden="true" />
          <span className={styles.NavRowLabel}>{backLabel}</span>
        </NavLink>
      </CollapsedLabelPopover>

      {!isCollapsed && (
        <div className={styles.WorkspacePill}>
          <span className={styles.WorkspaceAvatar}>{orgInitials}</span>
          <div className={styles.WorkspaceMeta}>
            <span className={styles.WorkspaceName} title={orgName}>
              {orgName ?? 'Loading…'}
            </span>
            {(arrLabel || openInvoices > 0) && (
              <span className={styles.WorkspaceStats}>
                {arrLabel && (
                  <span className={styles.WorkspaceStat}>
                    <span className={styles.WorkspaceStatLabel}>ARR</span>
                    <span className={styles.WorkspaceStatValue}>{arrLabel}</span>
                  </span>
                )}
                {openInvoices > 0 && (
                  <span className={styles.WorkspaceStat}>
                    <span className={styles.WorkspaceStatLabel}>OPEN</span>
                    <span className={styles.WorkspaceStatValue}>
                      {openInvoices} {openInvoices === 1 ? 'inv.' : 'inv.'}
                    </span>
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.Section}>
        {!isCollapsed && <div className={styles.SectionTitle}>Workspace</div>}
        <ul className={styles.SectionList}>
          {overviewItems.map((item) => {
            const badge = badgeFor(item.id);
            return (
              <li key={item.id} className={styles.SectionItem}>
                <NavRow
                  to={`/clients/${orgId}/${item.tab}`}
                  icon={item.icon}
                  label={item.label}
                  badge={badge?.value}
                  badgeTone={badge?.tone}
                  isCollapsed={isCollapsed}
                  onNavigate={onNavigate}
                />
              </li>
            );
          })}
        </ul>
      </div>

      {historyItems.length > 0 && (
        <div className={styles.Section}>
          {!isCollapsed && <div className={styles.SectionTitle}>History</div>}
          <ul className={styles.SectionList}>
            {historyItems.map((item) => (
              <li key={item.id} className={styles.SectionItem}>
                <NavRow
                  to={`/clients/${orgId}/${item.tab}`}
                  icon={item.icon}
                  label={item.label}
                  isCollapsed={isCollapsed}
                  onNavigate={onNavigate}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface SettingsScopedNavProps {
  sections: SettingsNavSection[];
  /** Flat page list for the collapsed rail's hover flyout. */
  pages: NavChildDef[];
  /** The page the person came from; Back goes there. */
  backTo: string;
  isCollapsed: boolean;
  onNavigate: () => void;
}

/**
 * The sidebar while org settings is open: a way back, then every settings page
 * under a collapsible section heading. The section holding the open page starts
 * expanded and the rest start closed, so the list stays short. The collapsed rail keeps the back link and one
 * Org Settings row whose hover flyout lists the same pages.
 */
function SettingsScopedNav({
  sections,
  pages,
  backTo,
  isCollapsed,
  onNavigate,
}: SettingsScopedNavProps): JSX.Element {
  const { pathname } = useLocation();
  const active = findNavItem(sections, settingsPageFromPath(pathname) ?? '');
  const activePage = active?.item.value;
  const activeSection = active?.section.value;
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    activeSection ? { [activeSection]: true } : {},
  );
  // Landing on a page (a link elsewhere, the browser back button) opens its
  // section, so the highlighted row is never hidden. Collapsing the section you
  // are in sticks until the page changes.
  useEffect(() => {
    if (activeSection)
      setOpen((prev) => (prev[activeSection] ? prev : { ...prev, [activeSection]: true }));
  }, [activePage, activeSection]);
  const flyoutChildren = pages.map((child) => ({
    ...child,
    isActive: (path: string) =>
      findNavItem(sections, settingsPageFromPath(path) ?? '')?.item.path === child.path,
  }));

  return (
    <div className={styles.ScopedNav}>
      <CollapsedLabelPopover label="Back" enabled={isCollapsed}>
        <NavLink
          to={backTo}
          onClick={onNavigate}
          className={classNames(styles.BackLink, {
            [styles.BackLinkCollapsed]: isCollapsed,
          })}
        >
          <ChevronLeft aria-hidden="true" />
          <span className={styles.NavRowLabel}>Back</span>
        </NavLink>
      </CollapsedLabelPopover>

      {isCollapsed ? (
        <div className={styles.Section}>
          <ul className={styles.SectionList}>
            <li className={styles.SectionItem}>
              <NavItem
                item={{
                  id: 'settings',
                  label: 'Org Settings',
                  icon: SettingsIcon,
                  path: '/settings',
                  children: flyoutChildren,
                  isActive: () => true,
                }}
                badge={undefined}
                isCollapsed
                onNavigate={onNavigate}
              />
            </li>
          </ul>
        </div>
      ) : (
        sections.map((section) => {
          const isOpen = open[section.value] === true;
          const listId = `settings-section-${section.value}`;
          return (
            <div key={section.value} className={styles.Section}>
              <button
                type="button"
                className={styles.SectionToggle}
                aria-expanded={isOpen}
                aria-controls={listId}
                onClick={() => setOpen((prev) => ({ ...prev, [section.value]: !isOpen }))}
              >
                <span className={styles.SectionToggleLabel}>{section.label}</span>
                <ChevronDown
                  aria-hidden="true"
                  className={classNames(styles.SectionToggleIcon, {
                    [styles.SectionToggleIconOpen]: isOpen,
                  })}
                />
              </button>
              {isOpen && (
                <ul id={listId} className={styles.SectionList}>
                  {section.items.map((page) => (
                    <li key={page.value} className={styles.SectionItem}>
                      <Link
                        to={page.path}
                        onClick={onNavigate}
                        aria-current={activePage === page.value ? 'page' : undefined}
                        className={classNames(styles.SettingsLink, {
                          [styles.SettingsLinkActive]: activePage === page.value,
                        })}
                      >
                        <span className={styles.NavChildLabel}>{page.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

const MOBILE_QUERY = '(max-width: 767px)';

function readDesktopPref(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('sidebar:collapsed') === '1';
}

function initialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia(MOBILE_QUERY).matches) return true;
  return readDesktopPref();
}

export default function EnhancedSidebar(): JSX.Element {
  // userCollapsed tracks the user's preference for the persistent rail
  // (desktop) and the mobile-default-hidden state. The actual rendered
  // collapsed state is derived below: when the mobile drawer is open,
  // the rail floats as an overlay and should always show full labels,
  // regardless of the persisted collapsed preference.
  const [userCollapsed, setUserCollapsed] = useState(initialCollapsed);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  });
  const { isMobileOpen, closeMobileSidebar } = useSidebarUI();

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handle = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      // Crossing the breakpoint: enter mobile collapsed by default,
      // restore the user's desktop preference when going wide again.
      setUserCollapsed(e.matches ? true : readDesktopPref());
      // Going back to desktop should also dismiss any lingering mobile
      // overlay state so the sidebar doesn't stay pinned on top.
      if (!e.matches) closeMobileSidebar();
    };
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, [closeMobileSidebar]);

  const { logout, user } = useAuth();
  const { selectedTenant } = useTenant();
  const { hasFeature, tenantType, billingStatus, isMaster } = useTenantFeatures();
  const isTenantAdmin = useIsTenantAdmin();

  const orgMatch = useMatch('/clients/:id/*');
  // `/clients/workload` is a page of its own, not a client whose id is "workload".
  const scopedOrgId = orgMatch?.params.id === 'workload' ? undefined : orgMatch?.params.id;
  const inSettings = Boolean(useMatch('/settings/*'));
  // Remember the last page outside settings so settings' Back can return to it.
  const { pathname: currentPath, search: currentSearch } = useLocation();
  const [settingsBackTo, setSettingsBackTo] = useState<string>(() => readSettingsReturnTo() ?? '/');
  useEffect(() => {
    if (currentPath.startsWith('/settings')) return;
    writeSettingsReturnTo(currentPath + currentSearch);
    setSettingsBackTo(currentPath + currentSearch);
  }, [currentPath, currentSearch]);

  const { data: scopedOrgData } = useQuery<OrganizationQuery>(ORGANIZATION_QUERY, {
    variables: { id: scopedOrgId },
    skip: !scopedOrgId,
    fetchPolicy: 'cache-only',
  });
  const scopedOrg = scopedOrgData?.organization;

  const scopedCounts = useMemo(() => {
    if (!scopedOrg) return { active: 0, overdue: 0, openInvoices: 0 };
    const subs = scopedOrg.subscriptions ?? [];
    const active = subs.filter((s) => s.status === 'ACTIVE').length;
    const invoices = subs.flatMap((s) => s.invoices ?? []);
    const overdue = invoices.filter((i) => i.paymentStatus === 'OVERDUE').length;
    const openInvoices = invoices.filter(
      (i) => i.paymentStatus === 'OVERDUE' || i.paymentStatus === 'PENDING',
    ).length;
    return { active, overdue, openInvoices };
  }, [scopedOrg]);

  const scopedInitials = useMemo(() => getInitials(scopedOrg?.name), [scopedOrg?.name]);

  const { data: countsData } = useQuery<SidebarCountsQuery>(SIDEBAR_COUNTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore',
    skip: Boolean(scopedOrgId),
  });

  // Failed or held knowledge: only for workspaces with Support, and never in a client scope.
  const { data: knowledgeData } = useQuery(KNOWLEDGE_HEALTH_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore',
    skip: Boolean(scopedOrgId) || !hasFeature(TenantFeature.SUPPORT),
  });

  const badgesByItemId = useMemo(() => {
    const renewals = formatCount(countsData?.renewalActionStats?.pending);
    const finance = formatCount(countsData?.paginatedInvoices?.totalCount);
    const orgs = formatCount(countsData?.organizationsPaginated?.totalCount);
    const subs = formatCount(countsData?.subscriptions?.totalCount);
    const knowledge = formatCount(knowledgeData?.knowledgeHealth?.attention);
    return {
      'support-knowledge': knowledge ? { value: knowledge, tone: 'danger' as BadgeTone } : null,
      renewals: renewals ? { value: renewals, tone: 'danger' as BadgeTone } : null,
      finance: finance ? { value: finance, tone: 'danger' as BadgeTone } : null,
      organizations: orgs ? { value: orgs, tone: 'neutral' as BadgeTone } : null,
      subscriptions: subs ? { value: subs, tone: 'neutral' as BadgeTone } : null,
    };
  }, [countsData, knowledgeData]);

  const filteredSections = useMemo(() => {
    return sidebarSections
      .map((section) => {
        if (section.tenantTypes && !section.tenantTypes.includes(tenantType)) return null;
        if (section.feature && !hasFeature(section.feature)) return null;
        const isAllowed = (def: Pick<NavItemDef, 'feature' | 'anyFeature'>): boolean => {
          if (def.feature && !hasFeature(def.feature)) return false;
          if (def.anyFeature && !def.anyFeature.some((flag) => hasFeature(flag))) return false;
          return true;
        };
        const items = section.items.flatMap((item) => {
          if (item.tenantTypes && !item.tenantTypes.includes(tenantType)) return [];
          if (!isAllowed(item)) return [];
          if (!item.children) return [item];
          const children = item.children.filter(isAllowed);
          // A group whose children are all gated off has nowhere to go.
          if (children.length === 0) return [];
          // The row itself must land somewhere the tenant can open.
          const path = children.some((child) => child.path === item.path)
            ? item.path
            : children[0].path;
          return [{ ...item, path, children }];
        });
        if (items.length === 0) return null;
        return { ...section, items };
      })
      .filter((section): section is NavSectionDef => section !== null);
  }, [hasFeature, tenantType]);

  // Org settings takes over the whole sidebar (as a client page does): the
  // pages this member can open, grouped by section.
  const settingsNav = useMemo(() => {
    const sections = buildSettingsNav({
      taxEnabled: hasFeature(TenantFeature.TAX),
      checkinsEnabled: hasFeature(TenantFeature.CHECKINS),
      supportEnabled: hasFeature(TenantFeature.SUPPORT),
      crmEnabled: hasFeature(TenantFeature.CRM),
      productUsageEnabled: hasFeature(TenantFeature.PRODUCT_USAGE),
      isTenantAdmin,
      hasPlan: Boolean(billingStatus),
      isMaster,
    });
    const children: NavChildDef[] = sections.flatMap((section) =>
      section.items.map((page) => ({
        id: `settings-${page.value}`,
        label: page.label,
        path: page.path,
        group: section.label,
      })),
    );
    return { sections, children };
  }, [hasFeature, isTenantAdmin, billingStatus, isMaster]);

  const toggleCollapsed = () => {
    setUserCollapsed((prev) => {
      const next = !prev;
      // Persist desktop preference only — on mobile the rail re-collapses
      // every time the breakpoint is entered.
      if (typeof window !== 'undefined' && !isMobile) {
        window.localStorage.setItem('sidebar:collapsed', next ? '1' : '0');
      }
      return next;
    });
  };

  // Only collapse on navigation/backdrop tap on mobile — desktop users keep
  // their chosen rail width.
  const closeIfMobile = () => {
    if (isMobile) closeMobileSidebar();
  };
  const isMobileExpanded = isMobile && isMobileOpen;
  // When the mobile drawer is open the rail floats as a full-width
  // overlay; render it expanded so labels show. Otherwise honour the
  // user's preference (persisted on desktop, forced collapsed on mobile
  // when the drawer is closed — though in that case the rail is
  // display:none anyway, so the value only matters for the brief
  // transition frame).
  const isCollapsed = isMobileExpanded ? false : userCollapsed;

  useEffect(() => {
    const w = isMobile ? '0px' : isCollapsed ? '4rem' : '17.5rem';
    document.documentElement.style.setProperty('--app-sidebar-width', w);
    return () => {
      document.documentElement.style.removeProperty('--app-sidebar-width');
    };
  }, [isCollapsed, isMobile]);

  const userDisplayName = user?.name || user?.email?.split('@')[0] || 'User';
  const userEmail = user?.email ?? '';
  const userInitials = getInitials(userDisplayName);
  const envLabel = getEnvLabel();
  const [isReloadingFrontend, setIsReloadingFrontend] = useState(false);

  const reloadFrontend = () => {
    if (isReloadingFrontend) return;
    setIsReloadingFrontend(true);
    void forceFrontendReload();
  };

  return (
    <React.Fragment>
      {isMobileExpanded && (
        <div
          className={styles.MobileOverlay}
          onClick={closeIfMobile}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeIfMobile();
          }}
          role="button"
          tabIndex={0}
          aria-label="Close navigation"
        />
      )}

      <aside
        className={classNames(styles.Sidebar, {
          [styles.SidebarCollapsed]: isCollapsed,
          [styles.SidebarMobileExpanded]: isMobileExpanded,
        })}
        data-collapsed={isCollapsed ? 'true' : 'false'}
      >
        <header className={styles.Brand}>
          <NavLink to="/" className={styles.BrandLink} onClick={closeIfMobile}>
            <RevvlyMark className={styles.BrandMark} aria-hidden="true" />
            <span className={styles.BrandText}>
              <span className={styles.BrandWordmark}>Revvly</span>
              <span className={styles.BrandTagline}>Revenue OS</span>
            </span>
          </NavLink>

          <div className={styles.BrandTrailing}>
            {envLabel && (
              <span className={styles.EnvPill}>
                {isReloadingFrontend ? 'Reloading…' : envLabel}
                <button
                  type="button"
                  className={classNames(styles.EnvRefreshButton, {
                    [styles.EnvRefreshButtonSpinning]: isReloadingFrontend,
                  })}
                  onClick={reloadFrontend}
                  disabled={isReloadingFrontend}
                  aria-label="Discard cached frontend and load the latest version"
                  title="Discard cached frontend and load the latest version"
                >
                  <RefreshCw aria-hidden="true" />
                </button>
              </span>
            )}
          </div>

          <button
            type="button"
            className={styles.CollapseButton}
            onClick={isMobile ? closeMobileSidebar : toggleCollapsed}
            aria-label={
              isMobile ? 'Close navigation' : isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
            }
            title={isMobile ? 'Close' : isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? (
              <PanelLeftOpen aria-hidden="true" />
            ) : (
              <PanelLeftClose aria-hidden="true" />
            )}
          </button>
        </header>

        <nav className={styles.Nav} aria-label="Primary">
          {scopedOrgId ? (
            <OrgScopedNav
              orgId={scopedOrgId}
              orgName={scopedOrg?.name}
              orgInitials={scopedInitials}
              arrLabel={null}
              openInvoices={scopedCounts.openInvoices}
              activeSubs={scopedCounts.active}
              overdueInvoices={scopedCounts.overdue}
              isProspect={isProspectRecord(scopedOrg)}
              isCollapsed={isCollapsed}
              onNavigate={closeIfMobile}
            />
          ) : inSettings ? (
            <SettingsScopedNav
              sections={settingsNav.sections}
              pages={settingsNav.children}
              backTo={settingsBackTo}
              isCollapsed={isCollapsed}
              onNavigate={closeIfMobile}
            />
          ) : (
            filteredSections.map((section) => (
              <div key={section.id} className={styles.Section}>
                {!isCollapsed && <div className={styles.SectionTitle}>{section.title}</div>}
                <ul className={styles.SectionList}>
                  {section.items.map((item) => {
                    const badge = badgesByItemId[item.id as keyof typeof badgesByItemId];
                    return (
                      <li key={item.id} className={styles.SectionItem}>
                        <NavItem
                          item={item}
                          badge={badge}
                          isCollapsed={isCollapsed}
                          onNavigate={closeIfMobile}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </nav>

        <div className={styles.Footer}>
          {/* Self-hides for single-tenant users; the rail's icon-only state has
              no room for a name, so it stays out of there. */}
          {!isCollapsed && <TenantSwitcher variant="sidebar" />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={styles.UserPill}
                aria-label={`Account menu for ${userDisplayName}`}
              >
                <span className={styles.UserAvatar}>{userInitials}</span>
                <span className={styles.UserMeta}>
                  <span className={styles.UserName}>{userDisplayName}</span>
                  <span className={styles.UserEmail}>{userEmail}</span>
                </span>
                <span className={styles.UserMenuIcon} aria-hidden="true">
                  <MoreHorizontal />
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={styles.UserMenu}
              side={isCollapsed ? 'right' : 'top'}
              align="start"
            >
              <DropdownMenuItem asChild>
                <NavLink to="/account" className={styles.UserMenuLink}>
                  <User aria-hidden="true" />
                  <span>My Account Settings</span>
                </NavLink>
              </DropdownMenuItem>
              {websiteUrl && (
                <DropdownMenuItem asChild>
                  <a
                    href={`${websiteUrl}/docs`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.UserMenuLink}
                  >
                    <BookOpen aria-hidden="true" />
                    <span>Documentation</span>
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <NavLink to="/onboarding" className={styles.UserMenuLink}>
                  <ClipboardList aria-hidden="true" />
                  <span>Resume/Restart Onboarding</span>
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => logout({ returnTo: window.location.origin })}
              >
                <LogOut aria-hidden="true" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </React.Fragment>
  );
}
